import { useEffect, useMemo, useState } from 'react'
import { analyzeDeviceLogsWithAuthContext, type AnalyzeDeviceLogsResponse } from '../../services/turingApi'
import { buildPresenceActivityBars, extractPresenceActivityPoints } from './presenceActivityUtils'

type Credentials = { apiKey?: string; accessToken?: string; appId?: string } | undefined

const PREVIEW_BARS = [20, 40, 80, 100, 60, 30, 20, 50, 70, 20]

export function usePresenceActivityData(deviceSN?: string | null, credentials?: Credentials) {
  const hasRealtimeSource = Boolean(
    String(deviceSN || '').trim() && (credentials?.apiKey || credentials?.accessToken)
  )
  const [bars, setBars] = useState<number[]>(() => (hasRealtimeSource ? [] : PREVIEW_BARS))
  const [loading, setLoading] = useState<boolean>(hasRealtimeSource)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const sn = String(deviceSN || '').trim()
    const auth = credentials || {}
    const hasAuth = Boolean(auth.apiKey || auth.accessToken)

    if (!sn || !hasAuth) {
      setBars(PREVIEW_BARS)
      setLoading(false)
      setError(null)
      return () => {
        cancelled = true
      }
    }

    setLoading(true)
    setError(null)
    analyzeDeviceLogsWithAuthContext(auth, sn, 'week', 'detailed')
      .then((response: AnalyzeDeviceLogsResponse) => {
        if (cancelled) return
        const points = extractPresenceActivityPoints(response.recentEvents)
        const nextBars = buildPresenceActivityBars(points)
        setBars(nextBars)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setBars(new Array<number>(10).fill(0))
        setError(err instanceof Error ? err.message : 'Failed to load presence activity')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [credentials?.accessToken, credentials?.apiKey, credentials?.appId, deviceSN])

  const empty = useMemo(() => bars.every((value) => value <= 0), [bars])
  return { bars, loading, empty, error }
}
